import React, { useState, useEffect, useRef } from 'react';
import Button from '../../../../../../shared/components/ui/Button';

// Component for Executive Reason Cell (Section 10)
const ExecutiveReasonCell = ({
  row,
  mode,
  handleOpenReasonModal,
  priorityEscalationReasonsData,
  isLoadingReasons,
  canAddReason,
  onSubmitReasonInline,
  isSubmittingReason
}) => {
  // Extract sales_id from the row structure
  // The row might have sales_id, id, or user_id
  const salesId = row.sales_id || row.id || row.user_id || row.salesId || null;

  // Find reason for this specific sales_id from the fetched reasons
  const reasonForThisRow = priorityEscalationReasonsData?.data?.find(
    (reason) => reason.sales_id === salesId || reason.sales_id === Number(salesId)
  );

  // State for inline editing
  const [isEditingComment, setIsEditingComment] = useState(false);
  const [commentText, setCommentText] = useState(reasonForThisRow?.reason || '');

  // Track previous submitting state to detect when submission completes
  const prevIsSubmittingRef = useRef(isSubmittingReason);

  // Update commentText when reasonForThisRow changes and reset editing state after successful save
  useEffect(() => {
    if (reasonForThisRow?.reason) {
      setCommentText(reasonForThisRow.reason);
    } else {
      setCommentText('');
    }

    // Reset editing state when submission completes successfully
    if (prevIsSubmittingRef.current && !isSubmittingReason && isEditingComment) {
      setIsEditingComment(false);
    }
    prevIsSubmittingRef.current = isSubmittingReason;
  }, [reasonForThisRow?.reason, isSubmittingReason, isEditingComment]);

  const handleStartEditing = () => {
    setIsEditingComment(true);
    setCommentText(reasonForThisRow?.reason || '');
  };

  const handleCancelEditing = () => {
    setIsEditingComment(false);
    setCommentText(reasonForThisRow?.reason || '');
  };

  const handleSaveComment = () => {
    if (!commentText.trim()) {
      return;
    }
    // Pass reason ID if editing existing comment
    const reasonId = reasonForThisRow?.id || null;
    onSubmitReasonInline('Priority Escalation', commentText, null, null, salesId, reasonId);
    // Editing state will be reset in useEffect after successful save
  };

  const hasReason = reasonForThisRow?.reason;
  const isClickable = canAddReason && mode === 'dashboard' && !isEditingComment;

  return (
    <td className="px-4 py-3 text-sm text-gray-900 font-medium">
      {isLoadingReasons ? (
        <span className="text-gray-400">Loading...</span>
      ) : isEditingComment ? (
        <div className="space-y-2">
          <textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Enter your comment..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
            rows={3}
            autoFocus
          />
          <div className="flex gap-2">
            <Button
              onClick={handleSaveComment}
              displayType="secondary-red"
              disabled={!commentText.trim() || isSubmittingReason}
              className="text-xs px-3 py-1">
              {isSubmittingReason ? 'Saving...' : 'Save'}
            </Button>
            <Button
              onClick={handleCancelEditing}
              displayType="secondary"
              disabled={isSubmittingReason}
              className="text-xs px-3 py-1">
              Cancel
            </Button>
          </div>
        </div>
      ) : hasReason ? (
        <div
          onClick={isClickable ? handleStartEditing : undefined}
          className={`${
            isClickable ? 'cursor-pointer hover:bg-gray-100 p-2 rounded transition-colors' : ''
          }`}>
          {reasonForThisRow.reason}
        </div>
      ) : (
        <div
          onClick={isClickable ? handleStartEditing : undefined}
          className={`text-gray-400 italic ${
            isClickable
              ? 'cursor-pointer hover:bg-gray-100 p-2 rounded transition-colors hover:text-gray-600'
              : ''
          }`}>
          {isClickable ? 'Click here to add/edit reason' : '-'}
        </div>
      )}
    </td>
  );
};

export default ExecutiveReasonCell;
